/**
 * AskCopilot: a small "Ask" link beside something a page shows (a defect, an excursion, a chart, an alarm), which opens
 * the AI Casting Consultant with a question drafted and what it is about (lib/copilot/ask): the page, and the defect,
 * condition, period, window or report it names. The person reads the question and sends it; the context rides with
 * that first message (2026-09-28).
 *
 * Where the consultant's conversation shows in the right-hand panel (lib/copilot/availability panelContent), a plain
 * click opens the panel with the question drafted and the person stays on the page (2026-10-06); the link keeps its
 * address, so a click that opens it in a new tab or window still opens the consultant's page. Elsewhere the link opens
 * the page, as before.
 *
 * It shows only where the top bar's button opens the consultant's page (consultantLauncher(...).opensPage, as
 * CopilotLauncher decides), and only inside the app, under the copilot's provider: a view rendered alone (a test, a
 * standalone export) shows none. It is left out of a printed or captured report.
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { useCopilotAvailability } from "@/lib/api/copilot-imports";
import { consultantLauncher, panelContent } from "@/lib/copilot/availability";
import { askHref, openInPanel, type CopilotAbout } from "@/lib/copilot/ask";
import { useCopilot, useOptionalCopilot } from "@/lib/copilot/store";

export interface AskCopilotProps {
  /** The question drafted in the consultant's composer. */
  question: string;
  /** What it is about: the chip's words and the context the first message carries. */
  about: CopilotAbout;
  className?: string;
  /** Opens the panel's conversation on a plain click (where the panel shows it), instead of the consultant's page. */
  inPanel?: boolean;
}

export function AskCopilot(props: AskCopilotProps) {
  const copilot = useOptionalCopilot();
  // Outside the app's providers there is no consultant to ask (and nothing to read its availability with).
  if (!copilot) return null;
  return <AskWhereOpen {...props} />;
}

/** The link, where the consultant's page opens. */
function AskWhereOpen(props: AskCopilotProps) {
  const availability = useCopilotAvailability();
  const { pathname } = useLocation();
  if (!consultantLauncher(availability).opensPage) return null;
  return <AskCopilotLink {...props} inPanel={panelContent(availability, pathname) === "conversation"} />;
}

/** The link itself: a ghost link, the Sparkles and "Ask", named for what it asks about. */
export function AskCopilotLink({ question, about, className, inPanel = false }: AskCopilotProps) {
  const copilot = useOptionalCopilot();
  return (
    <Link
      to={askHref(question, about)}
      onClick={(e) => {
        if (inPanel && copilot) openInPanel(e, copilot.ask, { draft: question, about });
      }}
      aria-label={`Ask the consultant about ${about.label}`}
      title={question}
      data-ask-copilot=""
      data-no-print=""
      data-html2canvas-ignore="true"
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-on-surface-muted",
        "transition-colors hover:bg-surface-muted hover:text-on-surface",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <Sparkles aria-hidden className="size-3.5" />
      Ask
    </Link>
  );
}

export interface ConsultantLinkProps {
  question: string;
  about: CopilotAbout;
  className?: string;
  children: ReactNode;
}

/**
 * A link to the consultant a page words itself (the brief's "Ask the consultant" under a piece of evidence): the same
 * address as AskCopilot's, and in the app, where the panel shows the conversation, a plain click opens the panel with
 * the question instead. Outside the app's providers it is the plain link.
 */
export function ConsultantLink(props: ConsultantLinkProps) {
  const copilot = useOptionalCopilot();
  if (!copilot) {
    return (
      <Link to={askHref(props.question, props.about)} className={props.className}>
        {props.children}
      </Link>
    );
  }
  return <ConsultantLinkInApp {...props} />;
}

function ConsultantLinkInApp({ question, about, className, children }: ConsultantLinkProps) {
  const copilot = useCopilot();
  const { pathname } = useLocation();
  const inPanel = panelContent(useCopilotAvailability(), pathname) === "conversation";
  return (
    <Link
      to={askHref(question, about)}
      onClick={(e) => {
        if (inPanel) openInPanel(e, copilot.ask, { draft: question, about });
      }}
      className={className}
    >
      {children}
    </Link>
  );
}
